import { motion } from "framer-motion";

/**
 * PhoneMockup — a 3D-tilted iPhone-style frame that wraps arbitrary
 * children (real React components, not screenshots). Used on the
 * cinematic homepage to show off Radio, Chat, and Dashboard.
 *
 * The tilt is done with CSS 3D transforms so the animation stays 60fps.
 * children fill the screen; overflow is clipped by the rounded frame.
 */
export default function PhoneMockup({
  children,
  tilt = -12,          // degrees. Negative = lean left.
  rotateX = 8,
  translateY = 0,
  glow = true,
  className = "",
  delay = 0,
  floatRange = 12,
  testId,
}) {
  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 60, rotateY: tilt * 1.5, rotateX: rotateX * 1.5 }}
      whileInView={{ opacity: 1, y: 0, rotateY: tilt, rotateX }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{
        transformStyle: "preserve-3d",
        perspective: "1400px",
        transform: `translateY(${translateY}px)`,
      }}
      className={`relative ${className}`}
    >
      {/* Ember glow underneath */}
      {glow && (
        <div
          aria-hidden
          className="absolute -inset-6 blur-3xl opacity-40 -z-10"
          style={{
            background:
              "radial-gradient(60% 60% at 50% 50%, rgba(255,69,0,0.55), transparent 70%)",
          }}
        />
      )}

      {/* Gentle floating loop after entrance */}
      <motion.div
        animate={{ y: [0, -floatRange, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: delay + 0.5 }}
      >
        {/* Phone bezel */}
        <div className="relative w-[260px] sm:w-[300px] rounded-[42px] bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-black border border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)] p-[6px]">
          {/* Notch */}
          <div className="absolute left-1/2 -translate-x-1/2 top-2 h-5 w-24 bg-black rounded-b-2xl z-10 flex items-center justify-center gap-2">
            <div className="h-1 w-1 rounded-full bg-white/20" />
            <div className="h-2 w-2 rounded-full bg-white/10" />
          </div>
          {/* Screen */}
          <div className="relative rounded-[36px] overflow-hidden bg-[#0A0A0A] aspect-[9/19.5]">
            {children}
          </div>
          {/* Side buttons */}
          <div className="absolute -left-[3px] top-24 h-8 w-[3px] bg-white/10 rounded-r" />
          <div className="absolute -left-[3px] top-36 h-14 w-[3px] bg-white/10 rounded-r" />
          <div className="absolute -right-[3px] top-32 h-20 w-[3px] bg-white/10 rounded-l" />
        </div>
      </motion.div>
    </motion.div>
  );
}
